import { apiClient } from './api-client'
import type { FinanceFeatureMap } from '@/lib/finance'

export interface MoneyCount { count: number; total: number }
export interface SideSummary { paid: MoneyCount; pending: MoneyCount; overdue: MoneyCount }

export interface UpcomingEntry {
  id: string
  description: string
  type: 'INCOME' | 'EXPENSE'
  amount: number
  dueDate: string
  overdue: boolean
  daysUntilDue: number
  clientId: string | null
  caseId: string | null
  needsConfirmation: boolean
}

export interface EntitySummary {
  today: string
  receivable: SideSummary
  payable: SideSummary
  upcoming: UpcomingEntry[]
}

export interface OverdueTotals { receivable: MoneyCount; payable: MoneyCount }

export interface PeriodSummary {
  from: string
  to: string
  toReceive: MoneyCount
  receivedInPeriod: MoneyCount
  toPay: MoneyCount
  paidInPeriod: MoneyCount
  overdue: OverdueTotals
}

export interface DueAlerts {
  today: string
  days: number
  receivable: UpcomingEntry[]
  payable: UpcomingEntry[]
  overdue: OverdueTotals
}

export type RecurringStatus = 'ACTIVE' | 'PAUSED' | 'FINISHED' | 'CANCELLED'

export interface RecurringTemplate {
  id: string
  description: string
  type: 'INCOME' | 'EXPENSE'
  accountId: string
  clientId: string | null
  caseId: string | null
  totalAmount: number | null
  installmentAmount: number | null
  installments: number | null
  firstDueDate: string
  intervalMonths: number
  generatedCount: number
  status: RecurringStatus
  annualFeeId: string | null
  createdByKind: 'HUMAN' | 'AGENT'
}

export interface CreateRecurringTemplateDto {
  description: string
  type: 'INCOME' | 'EXPENSE'
  accountId: string
  clientId?: string
  caseId?: string
  installmentAmount?: number
  totalAmount?: number
  installments?: number
  firstDueDate: string
  intervalMonths?: number
  notes?: string
}

const data = <T,>(r: { data: { data: T } }) => r.data.data

/** Leituras resumidas e modelos recorrentes do financeiro (fase 6). */
export const financeSummaryService = {
  features: async (): Promise<FinanceFeatureMap> =>
    data<FinanceFeatureMap>(await apiClient.get('/api/finance-v2/features')),

  client: async (clientId: string): Promise<EntitySummary> =>
    data<EntitySummary>(await apiClient.get(`/api/finance-v2/summary/client/${clientId}`)),

  case: async (caseId: string): Promise<EntitySummary> =>
    data<EntitySummary>(await apiClient.get(`/api/finance-v2/summary/case/${caseId}`)),

  period: async (from?: string, to?: string): Promise<PeriodSummary> =>
    data<PeriodSummary>(await apiClient.get('/api/finance-v2/reports/simplified-summary', { params: { from, to } })),

  dueAlerts: async (days = 3): Promise<DueAlerts> =>
    data<DueAlerts>(await apiClient.get('/api/finance-v2/alerts/due', { params: { days } })),

  recurring: {
    list: async (): Promise<RecurringTemplate[]> =>
      data<{ templates: RecurringTemplate[] }>(await apiClient.get('/api/finance-v2/recurring-templates')).templates,
    create: async (dto: CreateRecurringTemplateDto): Promise<RecurringTemplate> =>
      data<{ template: RecurringTemplate }>(await apiClient.post('/api/finance-v2/recurring-templates', dto)).template,
    action: async (id: string, action: 'pause' | 'resume' | 'cancel'): Promise<RecurringTemplate> =>
      data<{ template: RecurringTemplate }>(await apiClient.post(`/api/finance-v2/recurring-templates/${id}/${action}`)).template,
    run: async (): Promise<{ templates: number; generated: number }> =>
      data<{ templates: number; generated: number }>(await apiClient.post('/api/finance-v2/recurring-templates/run')),
  },
}
